import { z } from 'zod';

import { DISCOVERY_FILTER_TARGETS } from '@revelio/core/global-ai-assistant/discovery-targets';

import {
  applyFiltersAppliedResult,
  applyFiltersMissingArtifactResult,
  applyFiltersNoneResolvedResult,
  applyFiltersPageChangedResult,
  turnAbortedNoOpResult,
} from './tool-result-copy';
import type { ClientToolDefinition } from './types';

const schema = z
  .object({
    filterArtifactId: z.string(),
    target: z.enum(['current_page', ...DISCOVERY_FILTER_TARGETS]),
    merge: z.boolean().optional(),
    /**
     * Set by the turn step's same-page backstop, never by the model: the page
     * the rewritten call was meant for.
     */
    expectedPageOptionId: z.string().optional(),
  })
  .passthrough();

export const applyFiltersTool: ClientToolDefinition<typeof schema> = {
  schema,
  async run(
    { filterArtifactId, target, merge, expectedPageOptionId },
    { artifacts, deps, signal }
  ) {
    if (
      expectedPageOptionId &&
      deps.getCurrentPageOptionId() !== expectedPageOptionId
    ) {
      return applyFiltersPageChangedResult(expectedPageOptionId);
    }
    const filterResponse = artifacts.get(filterArtifactId);
    if (filterResponse === undefined) {
      return applyFiltersMissingArtifactResult(filterArtifactId);
    }
    const digest = await deps.applyFilters({ filterResponse, target, merge });
    // The unresolved-artifact fallback inside `applyFilters` awaits a network
    // resolve and emits from there, so it can RESOLVE after a stop. Bail
    // before narrating an apply the user cancelled (see
    // ClientToolContext.signal).
    if (signal?.aborted) {
      return turnAbortedNoOpResult;
    }
    if (digest.applied.length === 0) {
      return applyFiltersNoneResolvedResult(digest);
    }
    // The geography-taxonomy note this artifact may carry is re-emitted by
    // the registry for every artifact-consuming tool (see
    // `withGeoTaxonomyNote` in registry.ts), not here.
    return applyFiltersAppliedResult(digest);
  },
};
